import {
  Button,
  Card,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
  theme,
  TreeSelect,
  type TreeSelectProps,
  Typography,
} from 'antd';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { catchError, EMPTY, of, switchMap, tap } from 'rxjs';
import { type Customer, type Meal, Site, SpecialEventMeal } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { AtomicOperation, useAtomicOperations, useQuery } from '../../../jsonapi-provider';
import { Status, weekDays } from '../../../types';
import { LocalDate } from '../../../utils/local-date';
import utils from '../../../utils/utils';
import type dayjs from 'dayjs';

const SUCCESS_ADD_MESSAGE = 'Special Event Meal added successfully';
const SUCCESS_UPDATE_MESSAGE = 'Special Event Meal updated successfully';

interface SpecialEventMealFormProps {
  meal: Meal;
  meals: Meal[];
  onSuccess: (specialEventMeal: SpecialEventMeal) => void;
  onCancel: () => void;
}

const SpecialEventMealForm = (props: SpecialEventMealFormProps): ReactNode => {
  const notification = useNotification();
  const [formInstance] = Form.useForm();
  const { token } = theme.useToken();
  const { meal, onSuccess, onCancel, meals } = props;
  const isEditMode = useMemo(() => !!meal.specialEventMeal?.id, [meal.specialEventMeal?.id]);

  const [selectedSites, setSelectedSites] = useState(
    meal.specialEventMeal?.availableSites?.map((s) => s.id) ?? []
  );
  const customers = useQuery<Customer[]>([
    'customer',
    {
      filter: 'isInactive==false',
      include: ['sites'],
      sort: 'name',
      page: {
        size: 10000,
      },
    },
  ]);
  const initialValues = useMemo(
    () => ({
      ...meal.specialEventMeal,
      meal: meal.id,
      dateRange: [meal.specialEventMeal?.startDate, meal.specialEventMeal?.endDate],
      availableWeekDays: utils.mapStringToWeekDays(
        meal.specialEventMeal?.availableWeekDays ?? '0000000'
      ),
    }),
    [meal.specialEventMeal]
  );
  const atomicOperations = useAtomicOperations();

  useEffect(() => {
    setSelectedSites(meal.specialEventMeal?.availableSites.map((site) => site.id) ?? []);
    formInstance.setFieldsValue(initialValues);
    formInstance.setFieldValue(
      'sitesIds',
      meal.specialEventMeal?.availableSites.map((site) => site.id)
    );
    formInstance.setFieldValue('status', meal.specialEventMeal?.status !== Status.Disabled);
  }, [initialValues]);

  const onSubmit = (
    submitted: SpecialEventMeal & {
      meal: string;
      availableWeekDays: string[];
      dateRange: [LocalDate, LocalDate];
      status: boolean | string;
    }
  ): void => {
    const specialEventMealToBeSaved = new SpecialEventMeal(
      submitted.id,
      submitted.dateRange[0],
      submitted.dateRange[1],
      utils.mapWeekDaysToString(submitted.availableWeekDays),
      submitted.price,
      selectedSites.map((id) => {
        const site = new Site();
        site.id = id;
        return site;
      }),
      typeof submitted.status === 'boolean' && submitted.status === true
        ? Status.Enabled
        : Status.Disabled
    );

    const specialEventMealOperation = isEditMode
      ? AtomicOperation.update(specialEventMealToBeSaved)
      : AtomicOperation.add(specialEventMealToBeSaved, undefined, [
          'meal',
          submitted.meal,
          'specialEventMeal',
        ]);
    atomicOperations
      .operate(Array.of<AtomicOperation<SpecialEventMeal>>(specialEventMealOperation))
      .pipe(
        switchMap(([me]) => of<SpecialEventMeal>(Object.assign(new SpecialEventMeal(), me))),
        tap(() => {
          notification.success({
            message: ' ',
            description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
          });
        }),
        tap(onSuccess),
        catchError((error: Error) => {
          notification.error({
            message: ' ',
            description: error.message,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="special-event-meal-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>
              {isEditMode ? meal?.name : 'New Special Event Meal'}
            </Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Meal" name="meal" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              disabled={isEditMode}
              options={
                isEditMode
                  ? [
                      { label: meal?.name, value: meal?.id },
                      ...meals.map((meal) => ({ label: meal.name, value: meal.id })),
                    ]
                  : meals.map((meal) => ({ label: meal.name, value: meal.id }))
              }
            />
          </Form.Item>
          <Form.Item
            label="Date range"
            name="dateRange"
            rules={[{ required: true }]}
            getValueProps={(value: [LocalDate, LocalDate]) => ({
              value: value?.map((d) => d.toDayjs()),
            })}
            normalize={(value) => value?.map((d: dayjs.Dayjs) => new LocalDate(d))}
          >
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item label="Price" name="price" rules={[{ required: true }]}>
            <InputNumber addonBefore="$" min={0} precision={2} />
          </Form.Item>
          <Form.Item name="availableWeekDays" label="Available week days">
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 300, maxWidth: 900 }}
              placeholder="Please select week days"
              options={weekDays.map((day) => ({ label: day, value: day }))}
            />
          </Form.Item>
          <Form.Item label="Sites" name="sitesIds">
            <TreeSelect
              allowClear
              showSearch
              defaultValue={['all']}
              treeNodeFilterProp="title"
              treeCheckable
              showCheckedStrategy={TreeSelect.SHOW_PARENT}
              multiple
              value={selectedSites}
              listHeight={350}
              treeDefaultExpandedKeys={['all']}
              style={{ minWidth: 350 }}
              onChange={(e) => {
                if (e.length && e[e.length - 1] === 'all') {
                  setSelectedSites([
                    'all',
                    ...(customers.data ?? [])
                      .map((cust) => [...(cust.sites ?? [])].map((site) => site.id))
                      .flat(),
                  ]);
                } else if (e.length === 0) {
                  setSelectedSites([]);
                } else {
                  setSelectedSites(
                    e
                      .filter((element) => element !== 'all')
                      .map((el) =>
                        el?.at(0) !== ' '
                          ? el
                          : (customers.data as any[])
                              .find((cust) => cust.id === el.trimStart())
                              .sites?.map((site: Site) => site.id)
                      )
                      .flat()
                  );
                }
              }}
              treeData={
                [
                  {
                    title: 'All sites',
                    value: 'all',
                    children: customers.data?.map((cust, _idx) => ({
                      value: ' ' + cust.id,
                      title: cust.name,
                      children: [...(cust.sites ?? [])].map((site) => ({
                        value: site.id,
                        title: site.name,
                      })),
                    })),
                  },
                ] as TreeSelectProps['treeData']
              }
              tagRender={({ value, label }) =>
                value === 'all' ? (
                  <Tag color="pink">{label}</Tag>
                ) : value?.[0] === ' ' ? (
                  <Tag color="purple">{label}</Tag>
                ) : (
                  <Tag>{label}</Tag>
                )
              }
            />
          </Form.Item>
          <Form.Item name="status" label="Enabled">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse', marginBlock: token.marginMD }}>
              <Button
                block
                htmlType="submit"
                type="primary"
                disabled={atomicOperations.isLoading}
                loading={atomicOperations.isLoading}
              >
                Save
              </Button>
              <Button block onClick={onCancel} disabled={atomicOperations.isLoading}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

export default SpecialEventMealForm;
